import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import {
  LayoutDashboard,
  FileVideo,
  Users,
  BarChart3,
  User,
  LogOut,
  History,
  Menu,
  Trophy,
  BookOpen,
  School,
} from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { cn } from "@/projects/lib/utils";
import { getAuth, setAuth } from "@/projects/lib/store";
import { useAuth } from "@/projects/lib/useStore";
import { refreshSessionFromProfile } from "@/projects/lib/authApi";
import {
  getAccessToken,
  getTenantId,
  setLocalTenantId,
} from "@/lib/storeManager";
import {
  startAccessTokenRefreshScheduler,
  stopAccessTokenRefreshScheduler,
} from "@/services/tokenRefresh";
import { Button } from "@/projects/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/projects/components/ui/sheet";
import { toast } from "@/projects/lib/toast";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/topics", label: "Topics", icon: BookOpen },
  { to: "/admin/pathshalas", label: "Pathshalas", icon: School },
  { to: "/admin/submissions", label: "Video Submissions", icon: FileVideo },
  { to: "/admin/reward", label: "Ready for Reward", icon: Trophy },
  { to: "/admin/teachers", label: "Teacher Management", icon: Users },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  // { to: "/admin/exports", label: "Export History", icon: Download },
  { to: "/admin/profile", label: "Profile", icon: User },
];

const teacherNav = [
  { to: "/teacher", label: "Dashboard", icon: LayoutDashboard, exact: true },
  {
    to: "/teacher/submissions",
    label: "My Assigned Submissions",
    icon: FileVideo,
  },
  { to: "/teacher/history", label: "Verification History", icon: History },
  { to: "/teacher/profile", label: "Profile", icon: User },
];

function clearSession() {
  stopAccessTokenRefreshScheduler();
  const tenant = getTenantId();
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userInfo");
  setAuth(null);
  if (tenant) {
    setLocalTenantId(tenant);
  }
}

export function DashboardLayout({
  role,
  children,
  title,
  breadcrumb,
}: {
  role: "admin" | "teacher";
  children: ReactNode;
  title: string;
  breadcrumb?: string[];
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const nav = role === "admin" ? adminNav : teacherNav;
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const ensureSession = async () => {
      const token = getAccessToken();
      const localAuth = getAuth();

      // Access is token-based; API authorization uses Bearer + X-Tenant-Id.
      if (!token) {
        clearSession();
        if (!cancelled) {
          navigate({
            to: role === "admin" ? "/login/admin" : "/login/teacher",
          });
        }
        return;
      }

      try {
        // Load profile for display only — do not gate on role.
        await refreshSessionFromProfile(role);
      } catch {
        if (cancelled) {
          return;
        }
        // Keep session if token is present; use cached name when available.
        if (!localAuth) {
          setAuth({
            role,
            id: "user",
            name: "User",
          });
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    void ensureSession();
    return () => {
      cancelled = true;
    };
  }, [role, navigate]);

  // Keep access token alive: refresh every 14 minutes (access TTL = 15 min).
  // Stop only on logout (clearSession), not on layout remount / route change.
  useEffect(() => {
    if (getAccessToken()) {
      startAccessTokenRefreshScheduler();
    }
  }, []);

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  const handleLogout = () => {
    clearSession();
    toast.success("Logged out successfully");
    navigate({ to: "/" });
  };

  if (checking) {
    return (
      <div className="bg-muted/30 text-muted-foreground flex min-h-screen items-center justify-center text-sm">
        Loading profile…
      </div>
    );
  }

  const SidebarBody = (
    <div className="flex h-full flex-col">
      <Link
        to="/"
        className="border-sidebar-border flex items-center gap-2 border-b px-5 py-4"
      >
        <BrandLogo className="h-9 w-9" />
        <div className="min-w-0">
          <div className="text-guj text-sidebar-foreground truncate text-sm font-bold">
            રાજનગર જ્ઞાનોત્સવ
          </div>
          <div className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
            {role === "admin" ? "Admin Panel" : "Teacher Panel"}
          </div>
        </div>
      </Link>
      <nav className="flex-1 space-y-1 p-3">
        {nav.map(item => {
          const active = isActive(item.to, item.exact);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-soft"
                  : "text-sidebar-foreground hover:bg-sidebar-accent"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-sidebar-border border-t p-3">
        <button
          onClick={handleLogout}
          className="text-sidebar-foreground hover:bg-sidebar-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
        <div className="flex items-center justify-center gap-2 mt-2">
            <span className="text-muted-foreground text-[8px] font-medium tracking-[0.18em] uppercase">
              Powered by
            </span>
            <a href="https://www.universal-software.com" target="_blank" className="!text-primary text-[10px] font-semibold" >
              Universal Software
            </a>
          </div>
      </div>
    </div>
  );

  return (
    <div className="bg-muted/30 flex min-h-screen w-full">
      <aside className="border-sidebar-border bg-sidebar sticky top-0 hidden h-screen w-64 shrink-0 border-r lg:block">
        {SidebarBody}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-background/80 sticky top-0 z-30 flex h-16 items-center gap-3 border-b px-4 backdrop-blur lg:px-8">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-sidebar w-72 p-0">
              {SidebarBody}
            </SheetContent>
          </Sheet>
          <div className="min-w-0 flex-1">
            {breadcrumb && breadcrumb.length > 0 && (
              <div className="text-muted-foreground text-xs">
                {breadcrumb.join(" / ")}
              </div>
            )}
            <h1 className="truncate text-lg font-semibold">{title}</h1>
          </div>
          <div className="hidden items-center gap-3 sm:flex">
            <div className="text-right text-sm">
              <div className="leading-tight font-medium">{auth?.name}</div>
              <div className="text-muted-foreground text-xs capitalize">
                {auth?.role}
              </div>
            </div>
            <div className="grid h-9 w-9 place-items-center rounded-full border-2 border-[#f0c14d]/50 bg-[#0f2a6b] text-sm font-semibold text-white shadow-sm">
              {auth?.name?.[0]?.toUpperCase() ?? "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
